import { Box, Typography, Chip, useTheme, alpha } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const GREEN = "#2e9d5a";
const AMBER = "#e0a11b";
const RED = "#e5484d";

/**
 * Plain-language read of a /simulate result — the value eCalc doesn't give:
 * tells a builder what the numbers mean (agility, endurance, headroom, breaches)
 * instead of leaving them to interpret raw gauges.
 */
const Verdict = ({ result }) => {
  const theme = useTheme();
  const sys = result?.system;
  if (!sys) return null;

  const twr = sys.thrust_to_weight;
  const thr = sys.hover_throttle_pct;
  const ft = sys.hover_flight_time_min;
  const warns = result.warnings || [];

  // Overall status: red if underpowered / hot throttle / any breach; amber if marginal.
  let status = "good";
  if (warns.length || twr == null || twr < 1.3 || (thr != null && thr > 85)) status = "bad";
  else if (twr < 1.7 || (thr != null && thr > 75) || (ft != null && ft < 6)) status = "warn";

  const headline =
    status === "bad"
      ? twr != null && twr < 1.3
        ? "Underpowered — this setup can barely lift itself"
        : "Runs past a limit — check the flags below"
      : status === "warn"
      ? "Flyable, but with trade-offs"
      : "Well-matched setup";

  const cfg = {
    good: { color: GREEN, Icon: CheckCircleOutlineIcon },
    warn: { color: AMBER, Icon: WarningAmberIcon },
    bad: { color: RED, Icon: ErrorOutlineIcon },
  }[status];

  // Interpretive chips.
  const chips = [];
  if (twr != null) {
    const agility =
      twr >= 4 ? "Very aggressive" : twr >= 2 ? "Agile / sporty" : twr >= 1.5 ? "Balanced" : twr >= 1.3 ? "Gentle" : "Can't hover well";
    chips.push({ label: `${agility} · ${twr}:1 TWR`, color: twr >= 1.5 ? GREEN : twr >= 1.3 ? AMBER : RED });
  }
  if (thr != null)
    chips.push({
      label: `${thr}% hover${thr > 80 ? " · little headroom" : thr < 40 ? " · lots of headroom" : ""}`,
      color: thr <= 65 ? GREEN : thr <= 80 ? AMBER : RED,
    });
  if (ft != null)
    chips.push({ label: `~${ft} min hover`, color: ft >= 12 ? GREEN : ft >= 6 ? AMBER : RED });
  if (sys.specific_thrust_hover_g_per_w != null)
    chips.push({
      label: `${sys.specific_thrust_hover_g_per_w} g/W`,
      color: sys.specific_thrust_hover_g_per_w >= 6 ? GREEN : sys.specific_thrust_hover_g_per_w >= 3 ? AMBER : RED,
    });

  return (
    <Box
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 2,
        border: `1px solid ${alpha(cfg.color, 0.5)}`,
        backgroundColor: alpha(cfg.color, theme.palette.mode === "dark" ? 0.12 : 0.08),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <cfg.Icon sx={{ color: cfg.color }} />
        <Typography sx={{ fontWeight: 700, color: theme.palette.text.primary }}>{headline}</Typography>
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1.25 }}>
        {chips.map((c) => (
          <Chip
            key={c.label}
            label={c.label}
            size="small"
            sx={{ backgroundColor: alpha(c.color, 0.15), color: c.color, fontWeight: 600 }}
          />
        ))}
      </Box>
      {warns.length > 0 && (
        <Box sx={{ mt: 1.25 }}>
          {warns.map((w, i) => (
            <Typography key={i} variant="body2" sx={{ color: RED, display: "flex", gap: 0.75 }}>
              <span>•</span>
              {w}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default Verdict;
